import { Trash2, X, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Sticky action bar that appears at the top of a list whenever one or
 * more rows are selected. Handles the bulk-delete confirmation +
 * sequential fan-out so we can reuse the existing single-delete
 * endpoints without a backend change (each list is small — typically
 * <100 rows — so parallel calls are fine).
 *
 * Props:
 *   - count: number of rows currently selected
 *   - label: singular noun (e.g. "arrival", "program booking")
 *   - onClear: () => void  — dismiss the selection
 *   - onDelete: async () => void  — parent runs the actual delete work
 *
 * The bar auto-scrolls into view when it first appears and pins to
 * the top of its scrollable ancestor via `sticky top-0 z-30`.
 */
export default function BulkActionBar({ count, label = "row", onClear, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (count === 0) return null;

  const run = async () => {
    setBusy(true);
    try {
      await onDelete();
      toast.success(`${count} ${label}${count === 1 ? "" : "s"} moved to Recycle Bin`);
      onClear?.();
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="sticky top-0 z-30 -mx-2 mb-2" data-testid="bulk-action-bar">
      <div className="mx-2 p-3 rounded-lg bg-brand text-white shadow-lg flex flex-wrap items-center gap-3">
        <div className="text-sm font-semibold flex-1">
          <span data-testid="bulk-count">{count}</span> {label}{count === 1 ? "" : "s"} selected
        </div>
        <button data-testid="bulk-clear" onClick={onClear} disabled={busy}
          className="text-xs px-3 py-1.5 rounded border border-white/40 hover:bg-white/10 flex items-center gap-1 disabled:opacity-50">
          <X size={12}/> Clear
        </button>
        {!confirming ? (
          <button data-testid="bulk-delete" onClick={() => setConfirming(true)}
            className="text-xs px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 font-semibold flex items-center gap-1">
            <Trash2 size={12}/> Delete selected
          </button>
        ) : (
          <>
            <span className="text-xs italic">Sure? Sent to Recycle Bin.</span>
            <button data-testid="bulk-confirm" onClick={run} disabled={busy}
              className="text-xs px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 font-semibold flex items-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
              {busy ? "Deleting…" : `Yes, delete ${count}`}
            </button>
            <button data-testid="bulk-cancel" onClick={() => setConfirming(false)} disabled={busy}
              className="text-xs px-3 py-1.5 rounded border border-white/40 hover:bg-white/10">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
